import { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import {
  ExclamationTriangleIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { ALERT_SEVERITY_COLORS } from '../constants';

/**
 * Alert banner component for displaying active critical alerts.
 * Uses ARIA live region for accessibility.
 */
export default function AlertBanner({ alerts = [], onAcknowledge, onDismiss }) {
  const bannerRef = useRef(null);
  const [dismissedIds, setDismissedIds] = useState(new Set());

  // Filter to show only critical unacknowledged alerts
  const activeAlerts = alerts.filter(
    (alert) =>
      alert.severity === 'critical' &&
      !alert.acknowledged_at &&
      !alert.resolved_at &&
      !dismissedIds.has(alert.id)
  );

  // Animate banner on mount/update
  useEffect(() => {
    if (bannerRef.current && activeAlerts.length > 0) {
      gsap.fromTo(
        bannerRef.current,
        { opacity: 0, y: -10 },
        { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }
      );
    }
  }, [activeAlerts.length]);

  // Handle dismiss (temporarily hide)
  const handleDismiss = (alertId) => {
    setDismissedIds((prev) => new Set([...prev, alertId]));
    onDismiss && onDismiss(alertId);
  };

  // Handle acknowledge (permanent)
  const handleAcknowledge = (alertId) => {
    onAcknowledge && onAcknowledge(alertId);
  };

  if (activeAlerts.length === 0) {
    return null;
  }

  // Show most critical alert
  const topAlert = activeAlerts[0];
  const alertColor = ALERT_SEVERITY_COLORS[topAlert.severity] || ALERT_SEVERITY_COLORS.warning;

  return (
    <div
      ref={bannerRef}
      className="alert-banner"
      style={{ '--alert-color': alertColor }}
      role="alert"
      aria-live="assertive"
    >
      <div className="alert-banner-content">
        <ExclamationTriangleIcon className="alert-icon" />
        <div className="alert-text">
          <span className="alert-severity">{topAlert.severity.toUpperCase()}</span>
          <span className="alert-message">{topAlert.message}</span>
          {topAlert.machine && (
            <span className="alert-machine">on {topAlert.machine}</span>
          )}
        </div>
        {activeAlerts.length > 1 && (
          <span className="alert-count">+{activeAlerts.length - 1} more</span>
        )}
      </div>

      <div className="alert-banner-actions">
        <button
          className="btn-ack"
          onClick={() => handleAcknowledge(topAlert.id)}
          title="Acknowledge"
          aria-label="Acknowledge alert"
        >
          <CheckIcon className="icon-sm" />
          Ack
        </button>
        <button
          className="btn-dismiss"
          onClick={() => handleDismiss(topAlert.id)}
          title="Dismiss temporarily"
          aria-label="Dismiss alert"
        >
          <XMarkIcon className="icon-sm" />
        </button>
      </div>
    </div>
  );
}

/**
 * Alert history list component.
 */
export function AlertHistory({ alerts = [], onAcknowledge }) {
  if (!alerts || alerts.length === 0) {
    return (
      <div className="alert-history-empty">
        No recent alerts
      </div>
    );
  }

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  return (
    <div className="alert-history">
      <h4>Recent Alerts</h4>
      <div className="alert-list">
        {alerts.map((alert) => {
          const color = ALERT_SEVERITY_COLORS[alert.severity] || ALERT_SEVERITY_COLORS.info;
          const isResolved = !!alert.resolved_at;
          const isAcked = !!alert.acknowledged_at;

          return (
            <div
              key={alert.id}
              className={`alert-item ${isResolved ? 'resolved' : ''} ${isAcked ? 'acknowledged' : ''}`}
              style={{ '--alert-color': color }}
            >
              <div className="alert-item-indicator" />
              <div className="alert-item-content">
                <div className="alert-item-header">
                  <span className="alert-rule">{alert.rule_name}</span>
                  <span className="alert-time">{formatTime(alert.fired_at)}</span>
                </div>
                <div className="alert-item-message">{alert.message}</div>
                {alert.machine && (
                  <span className="alert-item-machine">{alert.machine}</span>
                )}
                {isResolved && (
                  <span className="alert-resolved-badge">Resolved</span>
                )}
                {isAcked && !isResolved && (
                  <span className="alert-acked-badge">
                    Acknowledged by {alert.acknowledged_by}
                  </span>
                )}
              </div>
              {!isAcked && !isResolved && (
                <button
                  className="btn-ack-small"
                  onClick={() => onAcknowledge && onAcknowledge(alert.id)}
                  title="Acknowledge"
                >
                  <CheckIcon className="icon-sm" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
